import React, { useState, useEffect } from "react";
import {
	Card,
	CardContent,
	Typography,
	TextField,
	Button,
	MenuItem,
	AppBar,
	Toolbar,
	Avatar,
	Box,
	CircularProgress,
	Container,
	IconButton,
	Drawer,
	Grid2,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";
import DeleteIcon from "@mui/icons-material/Delete";
import { setUser } from "../features/UserSlice";
import AccountCircle from "@mui/icons-material/AccountCircle";
import logo from "../Malayan.png";
import { sideMenuList, toggleDrawer } from "../utility/utils";
import { createUser, deleteUser, fetchUsers, fetchAllUsers } from "../utility/api"; // Import API functions
import { DataGrid } from '@mui/x-data-grid'; // Import DataGrid

const UserCreationForm = () => {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [formData, setFormData] = useState({
		username: "",
		firstName: "",
		middleName: "",
		lastName: "",
		userInitial: "",
		email: "",
		mobileNumber: "",
		role: "",
		reportingTo: "",
	});

	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [users, setUsers] = useState([]);
	const [myUsers, setMyUsers] = useState([]);
	const user = useSelector((state) => state.user.user);
	const [loadingUsers, setLoadingUsers] = useState(false);
	// { row, assignmentCount: null | number, deleting: boolean } while open
	const [deleteDialog, setDeleteDialog] = useState(null);

	const roles = ["Maker", "Manager", "Adjuster", "SysAdmin"];
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
	const validateMobile = (mobileNumber) => /^[0-9]{9,10}$/.test(mobileNumber);

	const handleMenuItemClick = (item) => {
		switch (item) {
			case "Create User":
				navigate("/createUser");
				break;
			case "My Assignments":
			case "All Assignments":
				navigate("/home");
				break;
			case "New Assignment":
				navigate("/claim/New");
				break;
			case "Data Reports":
				navigate("/data-reports");
				break;
			case "Change Password":
				navigate("/changePassword");
				break;
			case "Profile":
				navigate("/profile");
				break;
			case "Logout":
				handleLogout();
				break;
			default:
				break;
		}
	};

	const handleLogout = () => {
		dispatch(setUser(null));
		navigate("/");
	};

	const loadMyUsers = async () => {
		const data = await fetchAllUsers();
		setMyUsers(data);
	};

	useEffect(() => {
		if (formData.role === "Adjuster") {
			fetchReportingManagers();
		} else {
			setUsers([]);
		}

		loadMyUsers();
	}, [formData.role]);

	const openDeleteDialog = (row) => {
		setDeleteDialog({ row, assignmentCount: null, deleting: false });
	};

	const closeDeleteDialog = () => {
		setDeleteDialog(null);
	};

	const confirmDeleteUser = async () => {
		if (!deleteDialog || deleteDialog.deleting) return;
		const { row, assignmentCount } = deleteDialog;
		setDeleteDialog((prev) => ({ ...prev, deleting: true }));
		try {
			// Second confirmation (assignmentCount known) forces the delete
			await deleteUser(row.username, assignmentCount > 0);
			await loadMyUsers();
			setDeleteDialog(null);
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				error?.response?.data?.Message ||
				"";
			if (
				error?.response?.status === 409 &&
				message.startsWith("USER HAS ASSIGNMENTS")
			) {
				// Switch the dialog into the "user has assignments" warning
				const count = Number(message.split(":")[1]) || 0;
				setDeleteDialog((prev) => ({
					...prev,
					assignmentCount: count,
					deleting: false,
				}));
				return;
			}
			alert("Failed to delete user. Please try again.");
			setDeleteDialog((prev) =>
				prev ? { ...prev, deleting: false } : prev
			);
		}
	};

	const fetchReportingManagers = async () => {
		setLoadingUsers(true);
		try {
			const data = await fetchUsers("Manager");
			console.log("users : ", data);
			setUsers(data);
		} catch (error) {
			alert("Failed to fetch reporting managers. Please try again.");
		} finally {
			setLoadingUsers(false);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		console.log("name : value:", name, value);
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (isSubmitting) return;

		let validationErrors = {};
		if (!formData.firstName.trim())
			validationErrors.firstName = "First name is required.";
		if (!formData.lastName.trim())
			validationErrors.lastName = "Last name is required.";
		if (!formData.userInitial.trim())
			validationErrors.userInitial = "Initials is required.";
		if (!formData.email.trim() || !validateEmail(formData.email))
			validationErrors.email = "Valid email is required.";
		if (
			!formData.mobileNumber.trim() ||
			!validateMobile(formData.mobileNumber)
		)
			validationErrors.mobileNumber =
				"Valid 9 or 10-digit mobile number is required.";
		if (formData.role === "Adjuster" && !formData.reportingTo)
			validationErrors.reportingTo = "Reporting manager is required.";

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		setIsSubmitting(true);
		try {
			const userPayload = {
				...formData,
				username: formData.email,
				mobileNumber: `+60${formData.mobileNumber}`,
			};
			await createUser(userPayload);
			await loadMyUsers();
			alert("User created successfully!");
			setFormData({
				username: "",
				firstName: "",
				middleName: "",
				lastName: "",
				userInitial: "",
				email: "",
				mobileNumber: "",
				reportingTo: "",
				role: "",
			});
		} catch (error) {
			alert("Failed to create user. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const columns = [
        { field: 'id', headerName: 'ID', width: 60 },
        { field: 'name', headerName: 'Name', width: 270 },
		{ field: 'userInitial', headerName: 'Initials', width: 70 },
        { field: 'email', headerName: 'Email', width: 250 },
		{ field: 'mobile', headerName: 'Mobile', width: 180 },
        { field: 'role', headerName: 'Role', width: 150 },
        {
            field: 'actions',
            headerName: 'Action',
            width: 90,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isSelf = params.row.username === user?.userName;
                return (
                    <Tooltip
                        title={
                            isSelf
                                ? "You cannot delete your own account"
                                : "Delete user"
                        }
                    >
                        <span>
                            <IconButton
                                color="error"
                                size="small"
                                disabled={isSelf}
                                onClick={() => openDeleteDialog(params.row)}
                                aria-label={`Delete user ${params.row.name}`}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        }
    ];

    const rows = myUsers.map((listUser, index) => ({
        id: index + 1,
        name: listUser.firstName + " " + listUser.lastName,
		userInitial: listUser.userInitial,
        email: listUser.email,
		mobile: listUser.mobileNumber,
        role: listUser.role,
        username: listUser.userName || listUser.email,
    }));

	return (
		<Box
			sx={{
				width: "100%",
				margin: "auto",
				paddingBottom: 3,
			}}
		>
			<AppBar position="static">
				<Toolbar>
					<IconButton
						edge="start"
						color="inherit"
						aria-label="menu"
						onClick={toggleDrawer(setDrawerOpen)(true)}
					>
						<MenuIcon />
					</IconButton>
					<Box
						component="img"
						src={logo}
						alt="Logo"
						sx={{ height: 40, mr: 2, borderRadius: "50%" }}
					/>
					<Typography variant="h6" sx={{ flexGrow: 1 }}>
						My Users
					</Typography>
					<IconButton edge="end" color="inherit">
						<Avatar>
							<AccountCircle />
						</Avatar>
					</IconButton>
				</Toolbar>
			</AppBar>
			<Drawer
				anchor="left"
				open={drawerOpen}
				onClose={toggleDrawer(false)}
			>
				{sideMenuList(
					toggleDrawer(setDrawerOpen),
					handleMenuItemClick,
					user?.role
				)}
			</Drawer>
			<Container maxWidth="false" sx={{ mt: 4 }}>
			<Grid2 container spacing={2}>
			<Grid2 size={4}>
				<Card
					sx={{
						width: 400,
						padding: 2,
						borderRadius: 2,
						margin: "auto",
						height: 645
					}}
				>
					<CardContent>
						<Typography variant="h5" component="div" gutterBottom>
							User Creation Form
						</Typography>
						<Box
							component="form"
							onSubmit={handleSubmit}
							noValidate
						>
							<TextField
								label="First Name"
								name="firstName"
								fullWidth
								size="small"
								required
								margin="normal"
								value={formData.firstName}
								onChange={handleChange}
								error={!!errors.firstName}
								helperText={errors.firstName}
							/>
							<TextField
								label="Middle Name"
								name="middleName"
								fullWidth
								size="small"
								margin="normal"
								value={formData.middleName}
								onChange={handleChange}
							/>
							<TextField
								label="Last Name"
								name="lastName"
								fullWidth
								size="small"
								required
								margin="normal"
								value={formData.lastName}
								onChange={handleChange}
								error={!!errors.lastName}
								helperText={errors.lastName}
							/>
							<TextField
								label="Initials"
								name="userInitial"
								fullWidth
								size="small"
								required
								margin="normal"
								value={formData.userInitial}
								onChange={handleChange}
								error={!!errors.userInitial}
								helperText={errors.userInitial}
							/>
							<TextField
								label="Email"
								name="email"
								fullWidth
								size="small"
								required
								margin="normal"
								value={formData.email}
								onChange={handleChange}
								error={!!errors.email}
								helperText={errors.email}
							/>
							<TextField
								label="Mobile Number"
								name="mobileNumber"
								fullWidth
								size="small"
								required
								margin="normal"
								value={formData.mobileNumber}
								onChange={handleChange}
								error={!!errors.mobileNumber}
								helperText={errors.mobileNumber}
							/>
							<TextField
								label="Role"
								name="role"
								fullWidth
								size="small"
								select
								required
								margin="normal"
								value={formData.role}
								onChange={handleChange}
							>
								<MenuItem value="">-- Select --</MenuItem>
								{roles.map((role, index) => (
									<MenuItem key={index} value={role}>
										{role}
									</MenuItem>
								))}
							</TextField>
							{formData.role === "Adjuster" && (
								<TextField
									label="Reporting Manager"
									name="reportingTo"
									fullWidth
									size="small"
									select
									required
									margin="normal"
									value={formData.reportingTo}
									onChange={handleChange}
									disabled={loadingUsers}
								>
									<MenuItem value="">-- Select --</MenuItem>
									{users.map((user) => (
										<MenuItem
											key={user.userName}
											value={user.userName}
										>
											{user.name}
										</MenuItem>
									))}
								</TextField>
							)}
							<Button
								type="submit"
								variant="contained"
								color="primary"
								fullWidth
								sx={{ mt: 2 }}
								disabled={isSubmitting}
								startIcon={
									isSubmitting && (
										<CircularProgress size={20} />
									)
								}
							>
								{isSubmitting ? "Submitting..." : "Create User"}
							</Button>
						</Box>
					</CardContent>
				</Card>
			</Grid2>
			<Grid2 size={8}>
				<Card
                    sx={{
                        flex: 1,
                        padding: 2,
                        borderRadius: 2,
                    }}
                >
                    <CardContent>
                        <Typography variant="h5" component="div" gutterBottom>
                            Users List
                        </Typography>
                        <Box sx={{  width: '100%', height: 565 }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                pageSize={5}
                                rowsPerPageOptions={[5]}
                                // checkboxSelection
                                disableSelectionOnClick
                            />
                        </Box>
                    </CardContent>
                </Card>
				</Grid2>
			</Grid2>
			</Container>
			<Dialog
				open={!!deleteDialog}
				onClose={deleteDialog?.deleting ? undefined : closeDeleteDialog}
			>
				<DialogTitle>
					{deleteDialog?.assignmentCount > 0
						? "User has assignments"
						: "Delete user?"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{deleteDialog?.assignmentCount > 0
							? `${deleteDialog.row.name} (${deleteDialog.row.email}) is assigned to ${deleteDialog.assignmentCount} assignment(s). ` +
							  "The user will be deactivated but their assignment history will be kept. Are you sure you want to delete this user?"
							: `Delete user "${deleteDialog?.row.name}" (${deleteDialog?.row.email})? ` +
							  "The user will be deactivated, removed from this list and will no longer be able to sign in."}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={closeDeleteDialog}
						disabled={deleteDialog?.deleting}
					>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={confirmDeleteUser}
						disabled={deleteDialog?.deleting}
						startIcon={
							deleteDialog?.deleting && (
								<CircularProgress size={16} />
							)
						}
					>
						{deleteDialog?.assignmentCount > 0
							? "Delete anyway"
							: "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default UserCreationForm;
